import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  DistributionType,
  FiniteNumberOrPositiveInfinity
} from 'src/app/Interface/DataEntity/DataEntityVolumeForm_Interface';

export const DISTRIBUTION_TYPES: DistributionType[] = [
  'Automatic',
  'Normal',
  'Rectangular',
  'Trapezoidal',
  'Triangular',
  'StudentT'
];

export function finiteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return { finiteNumber: true };
    }

    return Number.isFinite(Number(control.value)) ? null : { finiteNumber: true };
  };
}

export function nonNegativeFiniteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    return Number.isFinite(value) && value >= 0 ? null : { nonNegative: true };
  };
}

export function positiveFiniteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    return Number.isFinite(value) && value > 0 ? null : { positive: true };
  };
}

export function finiteNumberOrPositiveInfinityValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    normalizeDoFOrNull(control.value) === null ? { finiteNumberOrInfinity: true } : null;
}

export function enumValueValidator(allowedValues: number[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    allowedValues.indexOf(Number(control.value)) >= 0 ? null : { enum: true };
}

export function distributionTypeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    DISTRIBUTION_TYPES.indexOf(control.value as DistributionType) >= 0
      ? null
      : { distributionType: true };
}

export function orderedNumberValidator(
  lowerControlName: string,
  upperControlName: string,
  errorKey: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const lower = Number(group.get(lowerControlName) && group.get(lowerControlName).value);
    const upper = Number(group.get(upperControlName) && group.get(upperControlName).value);

    if (!Number.isFinite(lower) || !Number.isFinite(upper) || lower <= upper) {
      return null;
    }

    return { [errorKey]: true };
  };
}

export function dateOrderValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const issue = parseLocalDate(group.get('IssueDateMiladi') && group.get('IssueDateMiladi').value);
    const calibration = parseLocalDate(
      group.get('CalibrationDateMiladi') && group.get('CalibrationDateMiladi').value
    );
    const recalibration = parseLocalDate(
      group.get('RecalibrationDateMiladi') && group.get('RecalibrationDateMiladi').value
    );
    const errors: ValidationErrors = {};

    if (issue !== null && calibration !== null && issue < calibration) {
      errors.issueBeforeCalibration = true;
    }
    if (calibration !== null && recalibration !== null && recalibration < calibration) {
      errors.recalibrationBeforeCalibration = true;
    }

    return Object.keys(errors).length ? errors : null;
  };
}

export function dateStringValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null;
    }

    return parseLocalDate(control.value) === null ? { dateString: true } : null;
  };
}

export function normalizeDoF(value: unknown): FiniteNumberOrPositiveInfinity {
  return normalizeDoFOrNull(value) ?? 0;
}

function normalizeDoFOrNull(value: unknown): FiniteNumberOrPositiveInfinity | null {
  if (value === 'Infinity' || value === Number.POSITIVE_INFINITY) {
    return 'Infinity';
  }

  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
}

function parseLocalDate(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }

  const match =
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?)?$/.exec(
      value
    );
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4] ?? 0);
  const minute = Number(match[5] ?? 0);
  const second = Number(match[6] ?? 0);
  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day ||
    parsed.getUTCHours() !== hour ||
    parsed.getUTCMinutes() !== minute ||
    parsed.getUTCSeconds() !== second
  ) {
    return null;
  }

  return year * 10000 + month * 100 + day;
}
